import { useEffect } from 'react';

import { CheckOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Card } from 'antd';
import { FormProvider, useForm } from 'react-hook-form';

import { useApproveTestingParticipantMutation } from '@/api/endpoints/schools/schoolSelectionPanel';
import { DataSection, TextAreaField } from '@/shared/components';
import { useApiRequest } from '@/shared/hooks';

import styles from './ApproveParticipantSection.module.scss';
import { APPROVE_PARTICIPANT_VALIDATION_SCHEMA } from './consts';
import type { IFormData } from './types';

interface IProp {
  handleCancel: VoidFunction;
  approvalComment: string | null;
  participantId: number;
  schoolId: number;
  setIsModalLoading: (state: boolean) => void;
}
export const ApproveParticipantSection = ({
  handleCancel,
  approvalComment,
  participantId,
  schoolId,
  setIsModalLoading,
}: IProp) => {
  const [approveParticipant, { isLoading: isParticipantApproving }] = useApproveTestingParticipantMutation();

  const sendApiRequest = useApiRequest();

  const methods = useForm<IFormData>({
    defaultValues: {
      approvalComment: approvalComment ?? '',
    },
    resolver: yupResolver(APPROVE_PARTICIPANT_VALIDATION_SCHEMA),
  });

  const { handleSubmit, reset } = methods;

  const approveParticipantHandler = handleSubmit(async ({ approvalComment }) => {
    setIsModalLoading(true);

    await sendApiRequest({
      successMessage: 'Данные успешно сохранены',
      apiRequestCallback: () =>
        approveParticipant({
          schoolId: Number(schoolId),
          participantId,
          approvalComment: approvalComment || null,
        }),
      successCallback: () => {
        handleCancel();
      },
    });

    setIsModalLoading(false);
  });

  useEffect(() => {
    reset({
      approvalComment: approvalComment ?? '',
    });
  }, [approvalComment]);

  return (
    <FormProvider {...methods}>
      <div className={styles.wrapper}>
        <div className={styles['form-field-wrapper']}>
          <DataSection titleText="Комментарий к одобрению">
            <TextAreaField
              fieldName="approvalComment"
              placeholder="Например: Отличные результаты тестирования, все проверки пройдены успешно..."
            />
          </DataSection>
          <p className={styles['field-annotation']}>
            Этот комментарий может быть отправлен участнику вместе с уведомлением об одобрении
          </p>
        </div>
        <Card className={styles['final-annotation-card']}>
          <p className={styles['final-annotation']}>
            <strong className={styles['final-annotation-strong']}>Важно:</strong> После одобрения участник будет
            зачислен в школу
          </p>
        </Card>
      </div>
      <div className={styles['buttons-wrapper']}>
        <Button
          className={styles.button}
          onClick={handleCancel}
        >
          Отмена
        </Button>
        <Button
          variant="solid"
          color="green"
          icon={<CheckOutlined />}
          className={styles.button}
          onClick={approveParticipantHandler}
          disabled={isParticipantApproving}
        >
          Одобрить участника
        </Button>
      </div>
    </FormProvider>
  );
};
